/** @jsxImportSource @compiled/react */
import React, { type FC } from 'react';
import { cssMap } from '@compiled/react';
// Jest uses classic JSX (`React.createElement`); TS `noUnusedLocals` does not treat JSX as reading `React`.
void React;

import Heading from '@atlaskit/heading';
import { token } from '@atlaskit/tokens';

import { usePageContentId } from '@confluence/page-context';
import { getEmojiFromPublishedPageProperties } from '@confluence/emoji-title/entry-points/transformer.ts';
import {
	SpaceOverviewQuery,
	type SpaceOverviewQuery$data,
	type SpaceOverviewQuery$variables,
} from '@confluence/space-overview-query/entry-points/SpaceOverviewQuery.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import type { SpaceOverviewSwitcherQuery$data } from '../__generated__/SpaceOverviewSwitcherQuery.graphql.ts';

import { SmartSpaceOverviewCoverPicture } from './SmartSpaceOverviewCoverPicture.tsx';
import { SmartSpaceOverviewSummary } from './SmartSpaceOverviewSummary.tsx';
import { PopularQuestions } from './popular-questions/PopularQuestions.tsx';
import { SmartSpaceOverviewHeader } from './SmartSpaceOverviewHeader.tsx';
import { SmartSpaceOverviewLogo } from './SmartSpaceOverviewLogo.tsx';
import { TopLinksCard } from './top-links-card/TopLinksCard.tsx';

export type SmartSpaceOverviewData = NonNullable<
	SpaceOverviewSwitcherQuery$data['confluence_smartSpaceOverview']
>;

type SmartSpaceOverviewProps = {
	spaceKey: string;
	smartSpaceOverviewData: SmartSpaceOverviewData;
};

export const SmartSpaceOverview: FC<SmartSpaceOverviewProps> = ({
	spaceKey,
	smartSpaceOverviewData,
}) => {
	const [contentId] = usePageContentId();

	const spaceOverviewQueryResult = useQueryInterop<
		SpaceOverviewQuery$data,
		SpaceOverviewQuery$variables
	>(SpaceOverviewQuery, {
		variables: {
			spaceKey,
			excludeOperations: true,
		},
	});

	const spaceData = spaceOverviewQueryResult.data;
	const isAdminLoading = spaceOverviewQueryResult.loading;
	const isAdmin = spaceData?.space?.currentUser?.isAdmin ?? false;
	const isPersonalSpace = spaceData?.space?.type === 'personal';
	const spaceId = spaceData?.space?.id ?? '';
	const spaceName = spaceData?.space?.name ?? '';
	const emoji = getEmojiFromPublishedPageProperties(spaceData?.space?.homepage ?? null);
	const spaceIconPath = spaceData?.space?.icon?.path;
	const hasEmoji = !!emoji || !!spaceIconPath;
	const topLinks = smartSpaceOverviewData.topLinks;

	if (!contentId || !spaceKey || isAdminLoading) {
		return null;
	}

	return (
		<div css={styles.container}>
			<SmartSpaceOverviewHeader />
			<div css={styles.coverWrapper}>
				<SmartSpaceOverviewCoverPicture
					contentId={contentId}
					isAdmin={isAdmin}
					hasEmoji={hasEmoji}
				/>
				<div css={styles.logoPositioner}>
					<SmartSpaceOverviewLogo
						spaceKey={spaceKey}
						spaceId={spaceId}
						isAdmin={isAdmin}
						isPersonalSpace={isPersonalSpace}
						emoji={emoji}
						spaceIconPath={spaceIconPath}
					/>
				</div>
			</div>
			<div css={styles.contentContainer}>
				{spaceName ? (
					<Heading as="h1" size="xxlarge">
						{spaceName}
					</Heading>
				) : null}
				<SmartSpaceOverviewSummary
					summaryData={smartSpaceOverviewData.summary}
					spaceKey={spaceKey}
				/>
				<TopLinksCard topLinks={topLinks} />
				<PopularQuestions faqData={smartSpaceOverviewData.faq} spaceKey={spaceKey} />
			</div>
		</div>
	);
};

const styles = cssMap({
	container: {
		backgroundColor: token('elevation.surface'),
		padding: token('space.600'),
		paddingTop: 0,
		maxWidth: '1200px',
		margin: '0 auto',
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.0'),
	},
	coverWrapper: {
		position: 'relative',
	},
	logoPositioner: {
		position: 'absolute',
		bottom: token('space.0'),
		left: token('space.400'),
		transform: 'translateY(50%)',
		zIndex: 1,
	},
	contentContainer: {
		paddingInline: token('space.400'),
		paddingBlockStart: token('space.800'),
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.400'),
	},
});
